/**
 * ShieldWall — Memory-Hard Proof-of-Work Engine
 *
 * Simulates memory-hard computation by chaining SHA-256 rounds that
 * reference previous blocks (similar to Argon2's memory matrix).
 * Each block depends on earlier blocks, forcing ~64KB memory per solve
 * and reducing GPU/ASIC advantage from ~100x to single-digit multiples.
 *
 * Production upgrade path: WASM Argon2id module via
 * https://github.com/nicolo-ribaudo/tc39-proposal-structs or
 * https://github.com/nicolo-ribaudo/argon2-browser
 *
 * References:
 *   - RFC 9106: Argon2 Memory-Hard Function
 *   - Research §1.2: Algorithm comparison
 *   - Research §1.4: Difficulty calibration strategies
 *
 * @module pow
 */

import { sha256 } from "./crypto";

export interface PoWConfig {
  /** Number of memory blocks to build per attempt */
  memoryBlocks: number;
  /** Maximum solve time before timeout (ms) */
  maxSolveTimeMs: number;
  /** Yield to main thread every N iterations */
  yieldInterval: number;
}

export interface PoWResult {
  nonce: number;
  hash: string;
  solveTimeMs: number;
  iterations: number;
  memoryBlocks: number;
  type: "memory_hard_sha256";
}

const DEFAULT_CONFIG: PoWConfig = {
  memoryBlocks: 64,
  maxSolveTimeMs: 1500,
  yieldInterval: 5,
};

/**
 * Solve a memory-hard proof-of-work challenge.
 *
 * The algorithm:
 *   1. For each nonce attempt, build an array of `memoryBlocks` blocks
 *   2. Each block = SHA-256(previous_block + back_reference_block)
 *   3. Final hash = SHA-256(block[0] + block[mid] + block[last] + nonce)
 *   4. If final hash starts with `difficulty` hex zeros, the PoW is solved
 *
 * This forces the solver to maintain all blocks in memory (can't discard),
 * which is what makes GPU parallelism less effective.
 *
 * @param challenge - Server-issued challenge string (typically a signed nonce)
 * @param difficulty - Number of leading hex zeros required
 * @param onProgress - Optional callback for UI progress updates (0-100)
 * @param config - Optional configuration overrides
 * @returns PoW solution result
 * @throws Error if solve time exceeds maxSolveTimeMs * 3
 */
export async function solveMemoryHardPoW(
  challenge: string,
  difficulty: number,
  onProgress?: (percent: number) => void,
  config: Partial<PoWConfig> = {}
): Promise<PoWResult> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const prefix = "0".repeat(difficulty);
  const start = performance.now();
  const deadline = cfg.maxSolveTimeMs * 3; // Hard deadline
  let nonce = 0;
  // SW-406: yield on a TIME budget (~one frame), not every N iterations.
  // setTimeout(0) is throttled to ~4ms/call, so "yield every 5 iterations" both
  // froze the main thread (no paints between bursts) and blew the deadline in
  // pure timer overhead. Time-based yielding keeps ~60fps responsiveness with a
  // fraction of the setTimeout calls, so the page never locks up while solving.
  const YIELD_MS = 12;
  let lastYield = start;

  while (performance.now() - start < deadline) {
    // Phase 1: Build memory blocks (simulated Argon2 fill phase)
    // Each block references the previous block AND an earlier block,
    // creating data dependencies that prevent memory-free shortcuts.
    const blocks: string[] = [];
    let prev = `${challenge}:${nonce}`;

    for (let i = 0; i < cfg.memoryBlocks; i++) {
      let backRefIndex = 0;
      if (i > 0) {
        const val = prev.charCodeAt(0);
        const offset = (val % Math.min(i, 8)) + 1;
        backRefIndex = Math.max(0, i - offset);
      }
      const backRef = i > 0 ? blocks[backRefIndex] : "";
      prev = await sha256(prev + backRef);
      blocks.push(prev);
    }

    // Phase 2: Final hash references multiple non-adjacent blocks
    // This ensures the solver kept the full block array in memory
    const mid = Math.floor(blocks.length / 2);
    const finalInput =
      blocks[0]! + blocks[mid]! + blocks[blocks.length - 1]! + nonce;
    const hash = await sha256(finalInput);

    if (hash.startsWith(prefix)) {
      return {
        nonce,
        hash,
        solveTimeMs: Math.round(performance.now() - start),
        iterations: nonce + 1,
        memoryBlocks: cfg.memoryBlocks,
        type: "memory_hard_sha256",
      };
    }

    nonce++;

    // Yield to the main thread once per frame budget so the browser can paint.
    const now = performance.now();
    if (now - lastYield >= YIELD_MS) {
      onProgress?.(Math.min(((now - start) / cfg.maxSolveTimeMs) * 90, 95));
      await new Promise((r) => setTimeout(r, 0));
      lastYield = performance.now();
    }
  }

  throw new Error(
    `PoW timeout: exceeded ${deadline}ms after ${nonce} iterations`
  );
}

/**
 * Verify a PoW solution (lightweight — server-side use).
 * Recomputes the memory-hard chain and checks the final hash.
 *
 * @param challenge - Original challenge string
 * @param solutionNonce - The nonce that was found
 * @param difficulty - Required leading hex zeros
 * @param memoryBlocks - Number of memory blocks (must match what client used)
 * @returns true if the solution is valid
 */
export async function verifyMemoryHardPoW(
  challenge: string,
  solutionNonce: number,
  difficulty: number,
  memoryBlocks: number = DEFAULT_CONFIG.memoryBlocks
): Promise<boolean> {
  const blocks: string[] = [];
  let prev = `${challenge}:${solutionNonce}`;

  for (let i = 0; i < memoryBlocks; i++) {
    let backRefIndex = 0;
    if (i > 0) {
      const val = prev.charCodeAt(0);
      const offset = (val % Math.min(i, 8)) + 1;
      backRefIndex = Math.max(0, i - offset);
    }
    const backRef = i > 0 ? blocks[backRefIndex] : "";
    prev = await sha256(prev + backRef);
    blocks.push(prev);
  }

  const mid = Math.floor(blocks.length / 2);
  const finalInput =
    blocks[0]! + blocks[mid]! + blocks[blocks.length - 1]! + solutionNonce;
  const hash = await sha256(finalInput);
  const prefix = "0".repeat(difficulty);

  return hash.startsWith(prefix);
}
